import { z } from 'zod';

const AttackVectorTypeSchema = z.enum(['SIGNAL', 'FINDING', 'PROMPT', 'ASSET']);

export type AttackVectorType = z.infer<typeof AttackVectorTypeSchema>;

export const AiDiscoveryPriorAttemptSchema = z.object({
  outcome: z.enum(['VERIFIED', 'FAILED']),
  failureReason: z.string().optional(),
  workingProtocolUrls: z.array(z.string()),
  recordedComponents: z.array(
    z.object({
      name: z.string(),
      type: z.string(),
    }),
  ),
  captureNotes: z.array(z.string()).optional(),
});

export const AiDiscoveryVerificationResultSchema = z.object({
  componentName: z.string(),
  componentType: z.string(),
  outcome: z.enum(['CONFIRMED', 'CONTRADICTED']),
  claim: z.string(),
  stimulus: z.string(),
  predictedSignal: z.string(),
  observedResponse: z.string(),
  invocationVisibility: z
    .enum(['hidden', 'user_visible', 'unknown'])
    .optional(),
  exchangeToolCallId: z.string().optional(),
});

export const AiDiscoveryVerificationRollupSchema = z.object({
  recorded: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  contradicted: z.number().int().nonnegative(),
  unverified: z.number().int().nonnegative(),
});

export type AiDiscoveryVerificationResultDto = z.infer<
  typeof AiDiscoveryVerificationResultSchema
>;

export type AiDiscoveryVerificationRollupDto = z.infer<
  typeof AiDiscoveryVerificationRollupSchema
>;

export const AiDiscoveryExecutionMetadataSchema = z.object({
  discoveryRunId: z.string().optional(),
  candidateId: z.string().optional(),
  componentId: z.string().optional(),
  componentType: z.string().optional(),
  interfaceId: z.string().nullable().optional(),
  targetProtocol: z.string().nullable().optional(),
  url: z.string().optional(),
  mcpToolName: z.string().nullable().optional(),
  verdictStatus: z.enum(['VERIFIED', 'FAILED']).optional(),
  failureReason: z.string().optional(),
  traceId: z.string().optional(),
  threadId: z.string().optional(),
  discoverInterface: z.boolean().optional(),
  discoveredToolCount: z.number().optional(),
  discoveredComponentTypes: z.array(z.string()).optional(),
  profileFieldsUpdated: z.array(z.string()).optional(),
  endpointsCreated: z.number().optional(),
  priorAttempt: AiDiscoveryPriorAttemptSchema.optional(),
  verificationResults: z.array(AiDiscoveryVerificationResultSchema).optional(),
  verificationRollup: AiDiscoveryVerificationRollupSchema.optional(),
});

export type AiDiscoveryPriorAttemptDto = z.infer<
  typeof AiDiscoveryPriorAttemptSchema
>;

export type AiDiscoveryExecutionMetadataDto = z.infer<
  typeof AiDiscoveryExecutionMetadataSchema
>;

export const AttackExecutionMetadataSchema = z.looseObject({
  vectorId: z.string().optional(),
  vectorType: AttackVectorTypeSchema.optional(),
  vectorPrompt: z.string().optional(),

  attackTraceId: z.string().optional(),
  classificationTraceId: z.string().optional(),

  classificationLabel: z.string().optional(),
  classificationSummary: z.string().optional(),
  retestFindingId: z.string().optional(),

  aiDiscovery: AiDiscoveryExecutionMetadataSchema.optional(),
});

export type AttackExecutionMetadataDto = z.infer<
  typeof AttackExecutionMetadataSchema
>;
